import { concat, NEVER, merge, of, EMPTY } from "rxjs";

import { vec2, vec4 } from "gl-matrix";

import { state } from "../index";

import { QUAD_WIDTH } from "../models";
import Town from "../town";
import TownPlay from "../town-play";
import { TileSet, GamePage, RainDrop, Directions } from "../interfaces";
import pathFinder from "../path-finder";
import { loadTexture } from "../helpers/webgl-helpers";
import { dimensionsMaintainingAspectWithMax, randomNumberBetween } from "../helpers/math-helpers";
import {
  callFunction$,
  cameraZoomTweenEvent$,
  delayEvent$,
  dialogueEvent$,
  dialogueEventAfter$,
  dialogueInteractionFn,
  dialogueInteractionEventFn,
  lockCameraPosition$,
  loopCharacterAlongPathXTimesEvent$,
  moveCharacterFromCurrentPosToPointEvent$,
  playSoundEvent$,
  saveToLocalStorage$,
  setProperty$,
  thatsItEvent$,
  translateTweenEvent$,
  translateTweenFromCameraEvent$,
  unlockCameraPosition$,
  updateCameraPosition$,
  updatePositionEvent$,
  updateInteraction$,
  waitFor$
} from "../town-observables";

import ScaryPresto from "../sprites/scary-presto";
import Henchperson from "../sprites/henchperson";
import RoyalGetem from "../sprites/royal-getem";
import Director from "../sprites/director";
import LordEgg from "../sprites/lord-egg";
import KingPresident from "../sprites/king-president";
import Metaorb from "../sprites/metaorb";
import Torch from "../sprites/torch";
import { updateRain, resizeRain } from "../weather";
import { MAX_DIMENSION } from "../index";
import AudioPlayback from "../audio-playback";

const numRainDrops = 250;

const torchSpeed = 100;

import mineTownTileMap from "../../assets/maps/mine-town.json";
import { throwIfEmpty } from "rxjs/operators";

const mineTownTileSet = mineTownTileMap.tilesets.find(
  (tileSet: TileSet) => tileSet.name === "terrain"
);
const normalTileSet = mineTownTileMap.tilesets.find(
  (tileSet: TileSet) => tileSet.name === "terrain-n"
);

const introUrl = require("../../assets/audio/music/minetown-intro.wav").default;
const fanfareUrl = require("../../assets/audio/sfx/explosion-6.wav").default;

export default class Minetown extends Town implements GamePage {
  skyRgba: vec4;
  rainCanvas: HTMLCanvasElement;
  rainDropPos: Array<RainDrop>;
  opacity: number;

  name = "minetown";

  private torches: Torch[];
  private prevTorchTime: number = 0;

  constructor() {
    super();

    this.torches = [];

    // Rain
    this.menu.classList.add("town-menu-wrapper");
    this.rainCanvas = document.createElement("canvas");
    this.rainCanvas.style.height = "100%";
    this.rainCanvas.style.width = "100%";
    this.rainCanvas.style.position = "fixed";
    this.menu.append(this.rainCanvas);

    this.skyRgba = [0.1, 0.1, 0.1, 1];
    this.opacity = 1;
    this.rainDropPos = new Array(numRainDrops).fill({
      x: -1,
      y: -1,
      length: 10,
      deltaY: 0,
      sinAngle: 0,
      cosAngle: 0,
      fallSpeed: 0
    });

    this.lightDropoffs = new Array(12).fill(0.4);
    this.dayLevel = 0.5;
  }

  async load(params) {
    await super.load(params);
    this.params = params;
    window.history.replaceState({}, document.title, window.location.pathname); // remove params
    this.resize();
    const audioPlayback = AudioPlayback.getInstance();
    audioPlayback.add("fanfare", fanfareUrl);

    // == Events / Game Save

    if (this.params.has("new-game")) {
      console.log("In minetown, attempting to create a new game...");
      state.happenedEvents["minetown"] = {};
      this.gameSaver.newGame();
    } else {
      console.log("In minetown, attempting to load a game...");
      const success = this.gameSaver.loadFirstSavedGame();
      if (!success) state.happenedEvents["minetown"] = {};
    }

    const ec = state.happenedEvents["minetown"];

    const { gameGl } = this;

    this.tileMap = mineTownTileMap;
    this.tileSet = mineTownTileSet;

    const mineTownTileSetImageUrl = await import(`../../assets/maps/${mineTownTileSet.image}`).then(
      i => i.default
    );

    const mineTownTileSetNormalTextureUrl = await import(
      `../../assets/maps/${normalTileSet.image}`
    ).then(i => i.default);

    this.texture = await loadTexture(gameGl, mineTownTileSetImageUrl);
    this.normalTexture = await loadTexture(gameGl, mineTownTileSetNormalTextureUrl);
    this.tileLayer = this.tileMap.layers.find(layer => layer.name === "main");

    this.configureStateWithCurrentMap();
    this.configureTileInAnimation();

    this.townPlay = new TownPlay(this.townTime$);

    // ====== Town Play stuff ================================================

    const { overlay, townTime$, gameSaver, townPlay } = this;
    let { t } = this;
    t = t.bind(this);

    // == ROYAL GETEMs

    const offscreenX = 26;
    const offscreenY = 1;
    const getemStartX = 24.9;
    const getemEndX = 21.5;
    const getemStartHighY = -12.0;
    const getemStartLowY = -14.0;

    const getem1 = new RoyalGetem(gameGl, "main");
    const getem2 = new RoyalGetem(gameGl, "main");
    const getem3 = new RoyalGetem(gameGl, "main");
    const getem4 = new RoyalGetem(gameGl, "main");
    const getem5 = new RoyalGetem(gameGl, "main");
    const getem6 = new RoyalGetem(gameGl, "main");

    getem1.updatePos(offscreenX, getemStartHighY);
    getem2.updatePos(offscreenX, getemStartHighY);
    getem3.updatePos(offscreenX, getemStartHighY);
    getem4.updatePos(offscreenX, getemStartLowY);
    getem5.updatePos(offscreenX, getemStartLowY);
    getem6.updatePos(offscreenX, getemStartLowY);

    const getems = [getem1, getem2, getem3, getem4, getem5, getem6];

    const getEmLoops$ = getems.map((getem, i) => {
      const y = i > 2 ? getemStartLowY : getemStartHighY;
      let directionFn = () => getem.setDirection(Directions.DownIdle);
      let done: any = of(true);
      if (i > 2) directionFn = () => getem.setDirection(Directions.UpIdle);
      if (i === 5) done = setProperty$(ec, "getems-done", true);

      const path = pathFinder(
        [getemStartX, y],
        [getemEndX - (i % 3), y],
        this.tileLayer,
        this.tileSet
      );

      return concat(
        waitFor$(ec, "oh-no", true, this.townTime$),
        delayEvent$(ec, `get-em-${i}-delay`, 750 * i),
        updatePositionEvent$(ec, `get-em-start-${i}`, getem, getemStartX, y),
        loopCharacterAlongPathXTimesEvent$(ec, `royal-getem-walk-${i}`, getem, path, townTime$, 1),
        callFunction$(getem, directionFn),
        done
      );
    });

    const getemsLoop$ = concat(
      waitFor$(ec, "oh-no", true, townTime$),
      dialogueEvent$(ec, t("royal-getems", "announcing"), overlay, null, "king-announced"),
      playSoundEvent$(ec, "play-fanfare", "fanfare")
    );

    // == KING PRESIDENT

    const kingStartY = -13.0;
    const kingStartX = 24.9;
    const kingPresident = new KingPresident(gameGl, "main");
    const kingEndX = 21.5;
    const kingEndY = -13.0;
    const kingDirectionFn = () => kingPresident.setDirection(Directions.LeftIdle);

    kingPresident.updatePos(offscreenX, kingStartY);

    const kingPath = pathFinder(
      [kingStartX, kingStartY],
      [kingEndX, kingEndY],
      this.tileLayer,
      this.tileSet
    );

    const kingPresidentLoop$ = concat(
      waitFor$(ec, "scary-walks-to-meet-prez", true, this.townTime$),
      updatePositionEvent$(ec, `king-pops-on-screen`, kingPresident, kingStartX, kingStartY),
      loopCharacterAlongPathXTimesEvent$(
        ec,
        `king-walks-to-scary`,
        kingPresident,
        kingPath,
        townTime$,
        1
      ),
      callFunction$(kingPresident, kingDirectionFn)
    );

    // == LORD EGG

    const eggStartY = -13.1;
    const eggStartX = 24.9;
    const lordEgg = new LordEgg(gameGl, "main");
    const eggEndX = 22.5;
    const eggEndY = -13.0;
    const eggDirectionFn = () => lordEgg.setDirection(Directions.LeftIdle);

    lordEgg.updatePos(offscreenX, eggStartY);

    const eggPath = pathFinder(
      [eggStartX, eggStartY],
      [eggEndX, eggEndY],
      this.tileLayer,
      this.tileSet
    );

    const lordEggLoop$ = concat(
      waitFor$(ec, "scary-walks-to-meet-prez", true, this.townTime$),
      delayEvent$(ec, "lord-egg-follows-prez", 1000),
      updatePositionEvent$(ec, `lord-egg-pops-on-screen`, lordEgg, eggStartX, eggStartY),
      loopCharacterAlongPathXTimesEvent$(
        ec,
        `lord-egg-walks-to-scary`,
        lordEgg,
        eggPath,
        townTime$,
        1
      ),
      callFunction$(lordEgg, eggDirectionFn)
    );

    // == HENCHPEOPLE

    const henchpeople: any[] = [];
    let luckyHenchperson = null;

    for (let i = 0; i < 12; i++) {
      if (i === 7) continue; // there's no mine in the 7th spot
      const henchperson1 = new Henchperson(gameGl, "main");
      const henchperson2 = new Henchperson(gameGl, "main");
      const henchperson3 = new Henchperson(gameGl, "main");
      const henchperson4 = new Henchperson(gameGl, "main");
      const henchperson5 = new Henchperson(gameGl, "main");
      const henchperson6 = new Henchperson(gameGl, "main");

      if (i === 0) luckyHenchperson = henchperson6;

      const im = i % 4;
      const leftX = im * 6 + 1.75;
      const rightX = im * 6 + 5.25;
      const y = -Math.floor(i / 4) * 8 - 3.5;
      henchperson1.updatePos(leftX, y - 0);
      henchperson2.updatePos(leftX, y - 1);
      henchperson3.updatePos(leftX, y - 2);
      henchperson1.setDirection(Directions.Right);
      henchperson2.setDirection(Directions.Right);
      henchperson3.setDirection(Directions.Right);

      henchperson4.updatePos(rightX, y - 0);
      henchperson5.updatePos(rightX, y - 1);
      henchperson6.updatePos(rightX, y - 2);

      henchperson4.setDirection(Directions.Left);
      henchperson5.setDirection(Directions.Left);
      henchperson6.setDirection(Directions.Left);

      henchpeople.push(
        henchperson1,
        henchperson2,
        henchperson3,
        henchperson4,
        henchperson5,
        henchperson6
      );
    }

    // == LUCKY HENCHPERSON

    const luckyStart = vec2.create();
    const luckyEnd: vec2 = [19.5, -13.0];

    vec2.copy(luckyStart, luckyHenchperson.pos);
    const luckyPath = pathFinder(luckyStart, luckyEnd, this.tileLayer, this.tileSet);
    const luckyHenchpersonLoop$ = concat(
      waitFor$(ec, "scary-says-we-havent", true, townTime$),
      waitFor$(ec, "path-to-lucky-henchperson", true, townTime$),
      delayEvent$(ec, "lucky-after-scary-stalls", 1500),
      callFunction$(luckyHenchperson, () => luckyHenchperson.changeAnimationSpeed(500)),
      dialogueEvent$(ec, t("henchperson-4", "hang-on"), overlay, null, "hang-on"),
      waitFor$(ec, "metaorb-falls", true, townTime$),
      callFunction$(luckyHenchperson, () => luckyHenchperson.setDirection(Directions.Down)),
      dialogueEvent$(ec, t("henchperson-4", "holy-moly"), overlay, null, "holy-moly"),
      loopCharacterAlongPathXTimesEvent$(
        ec,
        "lucky-runs",
        luckyHenchperson,
        luckyPath,
        townTime$,
        1
      ),
      callFunction$(luckyHenchperson, () => luckyHenchperson.setDirection(Directions.RightIdle))
    );

    // == METAORB

    const metaorb = new Metaorb(gameGl, "main");
    metaorb.updatePos(offscreenX, offscreenY);

    const metaorbLoop$ = concat(
      waitFor$(ec, "hang-on", true, townTime$),
      updatePositionEvent$(ec, "metaorb-appears", metaorb, luckyStart[0], luckyStart[1]),
      translateTweenEvent$(
        ec,
        "metaorb-falls",
        metaorb,
        luckyStart[0],
        luckyStart[1],
        luckyStart[0],
        luckyStart[1] - QUAD_WIDTH / 3,
        100
      ),
      waitFor$(ec, "holy-moly", true, townTime$),
      updatePositionEvent$(ec, "metaorb-appears", metaorb, offscreenX, offscreenY)
    );

    // == TORCHES

    const torches: Torch[] = [];
    // prettier-ignore
    const torchPositions = [
      [+3.5,-6.5] ,[+9.5,-6.5] ,[+15.5,-6.5] ,[+21.5,-6.5],
      [+3.5,-14.5],[+9.5,-14.5],[+15.5,-14.5],
      [+3.5,-22.5],[+9.5,-22.5],[+15.5,-22.5],[+21.5,-22.5]
    ];

    torchPositions.forEach(position => {
      const torch = new Torch(gameGl, "main");
      torch.updatePos(position[0], position[1]);
      torches.push(torch);
    });

    this.torches = torches;
    this.torches.forEach((torch, i) => {
      const pos: vec2 = torch.pos;
      const posOffset = i * 3;
      const colOffset = i * 4;
      this.lightPositions[posOffset + 0] = pos[0];
      this.lightPositions[posOffset + 1] = pos[1];
      this.lightPositions[posOffset + 2] = 0.5;

      this.lightColors[colOffset + 0] = 0.9;
      this.lightColors[colOffset + 1] = 0.2;
      this.lightColors[colOffset + 2] = 0.0;
      this.lightColors[colOffset + 3] = 1.0;
    });

    const startLocation: vec2 = [14.5, -16.5];
    const endLocation: vec2 = [10, -16.5];

    // == HENCHPEOPLE DIALOG

    const henchpeopleLoop$ = concat(
      waitFor$(ec, "scary-asks-everybody", true, townTime$),
      dialogueEvent$(ec, t("henchpeople", "ellipsis"), overlay),
      setProperty$(ec, "henchpeople-dont-respond", true),
      waitFor$(ec, "scary-asks-again", true, townTime$),
      dialogueEvent$(ec, t("henchpeople", "possibile"), overlay),
      setProperty$(ec, "henchpeople-respond-meekly", true)
    );

    // == DIRECTOR
    const director = new Director(gameGl, "main");
    director.updatePos(13.4, -15.15);

    // == SCARY PRESTO
    const scaryPresto: ScaryPresto = new ScaryPresto(gameGl, "main");
    const scaryMeetPrezPos = [20.5, -13.0];

    await Promise.all(
      [
        scaryPresto,
        ...henchpeople,
        ...torches,
        director,
        lordEgg,
        kingPresident,
        ...getems,
        metaorb
      ].map(c => c.load())
    );

    const path = pathFinder(startLocation, endLocation, this.tileLayer, this.tileSet);
    const path2 = pathFinder(endLocation, startLocation, this.tileLayer, this.tileSet);
    const combinedPath = [...path, ...path2];

    const walkAndTalk$ = merge(
      dialogueEvent$(ec, t("scary", "not-good"), overlay),
      loopCharacterAlongPathXTimesEvent$(ec, "scary-paces", scaryPresto, combinedPath, townTime$, 1)
    );

    const scaryLoop$ = concat(
      updatePositionEvent$(ec, "scary-start", scaryPresto, startLocation[0], startLocation[1]),
      setProperty$(state, "currentPlayer", scaryPresto),
      callFunction$(this.spriteRenderer, () => this.spriteRenderer.sort()),
      setProperty$(state, "inCinematic", true),
      dialogueEvent$(ec, t("scary", "today"), overlay),
      dialogueEvent$(ec, t("scary", "today-2"), overlay),
      setProperty$(ec, "scary-worries", true),
      walkAndTalk$,
      dialogueEvent$(ec, t("scary", "find-something"), overlay),
      dialogueEvent$(ec, t("scary", "still-time"), overlay),
      dialogueEvent$(ec, t("scary", "right-everybody"), overlay),
      setProperty$(ec, "scary-asks-everybody", true),
      waitFor$(ec, "henchpeople-dont-respond", true, townTime$),
      dialogueEvent$(ec, t("scary", "come-on"), overlay),
      setProperty$(ec, "scary-asks-again", true),
      waitFor$(ec, "henchpeople-respond-meekly", true, townTime$),
      dialogueEvent$(ec, t("scary", "ellipsis-1"), overlay),
      dialogueEvent$(ec, t("scary", "ellipsis-2"), overlay),
      dialogueEvent$(ec, t("scary", "ellipsis-3"), overlay),
      setProperty$(ec, "scary-realizes-he-is-doomed", true),
      dialogueEvent$(ec, t("scary", "screwed"), overlay),
      setProperty$(ec, "player-can-control-scary", true),
      dialogueEvent$(ec, t("scary", "dig-faster"), overlay),
      setProperty$(ec, "scary-asks-to-dig-faster", true),
      setProperty$(state, "inCinematic", false),
      waitFor$(ec, "king-president-arrives", true, this.townTime$),
      dialogueEvent$(ec, t("scary", "oh-no"), overlay),
      setProperty$(ec, "oh-no", true),
      waitFor$(ec, "getems-done", true, this.townTime$),
      moveCharacterFromCurrentPosToPointEvent$(
        ec,
        "scary-walks-to-meet-prez",
        scaryPresto,
        scaryMeetPrezPos[0],
        scaryMeetPrezPos[1],
        this.tileLayer,
        this.tileSet,
        this.townTime$
      ),
      callFunction$(scaryPresto, () => scaryPresto.setDirection(Directions.RightIdle))
    );

    // == SP-KP DIALOGUE
    const scaryKingConvoLoop$ = concat(
      waitFor$(ec, "king-announced", true, townTime$),
      dialogueEventAfter$(
        ec,
        t("king-president", "intro"),
        overlay,
        "king-walks-to-scary",
        townTime$
      ),
      dialogueEvent$(ec, t("scary", "confused"), overlay, null, "confused"),
      dialogueEvent$(ec, t("king-president", "i-say-lol"), overlay, null, "i-say-lol"),
      dialogueEvent$(ec, t("scary", "side-effects"), overlay),
      dialogueEvent$(ec, t("king-president", "uninterested"), overlay),
      dialogueEvent$(ec, t("scary", "scared"), overlay),
      dialogueEvent$(ec, t("king-president", "do-you-have-orbs"), overlay),
      dialogueEvent$(ec, t("scary", "so-close"), overlay),
      dialogueEvent$(ec, t("king-president", "now"), overlay),
      dialogueEvent$(ec, t("scary", "thing-is-1"), overlay),
      dialogueEvent$(ec, t("scary", "thing-is-2"), overlay, null, "thing-is-2"),
      dialogueEvent$(ec, t("scary", "thing-is-3"), overlay, null, "thing-is-3"),
      dialogueEvent$(ec, t("king-president", "stalling-1"), overlay, null, "stalling-1"),
      dialogueEvent$(ec, t("scary", "thing-is-4"), overlay),
      dialogueEvent$(ec, t("scary", "thing-is-5"), overlay),
      dialogueEvent$(ec, t("king-president", "stalling-2"), overlay, null, "stalling-2"),
      dialogueEvent$(ec, t("scary", "we-havent"), overlay, null, "scary-says-we-havent")
    );

    // == GAMESAVER

    const gameSaverLoop$ = concat(
      waitFor$(ec, "scary-asks-to-dig-faster", true, this.townTime$),
      saveToLocalStorage$(gameSaver),
      waitFor$(ec, "king-walks-to-scary", true, this.townTime$),
      saveToLocalStorage$(gameSaver)
    );

    // == CAMERA

    const camera = state.viz.camera;
    const cameraLoop$ = concat(
      setProperty$(this, "lockCameraToMapBounds", false),
      updateCameraPosition$(camera, -10000, -10000, 2),
      waitFor$(ec, "scary-worries", true, this.townTime$),
      setProperty$(this, "lockCameraToMapBounds", true),
      updateCameraPosition$(camera, 0, 0, 4),
      lockCameraPosition$(camera, scaryPresto),
      waitFor$(ec, "scary-asks-everybody", true, this.townTime$),
      cameraZoomTweenEvent$(ec, "zoom-out-on-henchmen", camera, () => camera.vector[2], 15, 1500),
      waitFor$(ec, "henchpeople-respond-meekly", true, this.townTime$),
      cameraZoomTweenEvent$(ec, "zoom-back-in-on-scary", camera, () => camera.vector[2], 1.2, 1500),
      waitFor$(ec, "scary-realizes-he-is-doomed", true, this.townTime$),
      updateCameraPosition$(camera, camera.vector[0], camera.vector[1], 0.8),
      waitFor$(ec, "player-can-control-scary", true, this.townTime$),
      updateCameraPosition$(camera, camera.vector[0], camera.vector[1], 10),
      waitFor$(ec, "king-announced", true, this.townTime$),
      setProperty$(state, "inCinematic", true),
      unlockCameraPosition$(camera),
      merge(
        cameraZoomTweenEvent$(ec, "zoom-out-to-getems", camera, () => camera.vector[2], 18, 3000),
        translateTweenFromCameraEvent$(ec, "path-to-getems", camera, 20.5, -12.5, 1500)
      ),
      waitFor$(ec, "confused", true, this.townTime$),
      updateCameraPosition$(camera, scaryMeetPrezPos[0], scaryMeetPrezPos[1], 5),
      waitFor$(ec, "thing-is-2", true, townTime$),
      merge(
        concat(
          delayEvent$(ec, "delay-lucky-zoom", 4000),
          cameraZoomTweenEvent$(
            ec,
            "zoom-to-lucky-henchperson",
            camera,
            () => camera.vector[2],
            3,
            4000
          )
        ),
        translateTweenFromCameraEvent$(
          ec,
          "path-to-lucky-henchperson",
          camera,
          luckyStart[0],
          luckyStart[1] + QUAD_WIDTH / 2,
          8000
        )
      ),
      lockCameraPosition$(camera, luckyHenchperson),
      waitFor$(ec, "lucky-runs", true, townTime$),
      unlockCameraPosition$(camera),
      merge(
        cameraZoomTweenEvent$(
          ec,
          "zoom-from-lucky-to-group",
          camera,
          () => camera.vector[2],
          12,
          3000
        ),
        translateTweenFromCameraEvent$(
          ec,
          "path-back-to-scary-after-lucky-runs",
          camera,
          () => scaryPresto.pos[0],
          () => scaryPresto.pos[1] + QUAD_WIDTH / 2,
          3000
        )
      ),
      lockCameraPosition$(camera, scaryPresto),
      setProperty$(state, "inCinematic", false),
      thatsItEvent$(ec, overlay),
      updateInteraction$(
        townPlay,
        "director-fran",
        dialogueInteractionFn(t("director-fran", "gods"), overlay)
      )
    );

    // +++ Add to townplay

    const generateHenchpersonLoop$ = (henchperson: Henchperson) => {
      return concat(
        waitFor$(ec, "scary-asks-to-dig-faster", true, this.townTime$),
        callFunction$(henchperson, () => henchperson.changeAnimationSpeed(200)),
        waitFor$(ec, "king-announced", true, this.townTime$),
        callFunction$(henchperson, () => henchperson.changeAnimationSpeed(100))
      );
    };

    this.townPlay.addCharacter("camera", state.viz.camera, cameraLoop$);
    torches.forEach((torch, i) => this.townPlay.addCharacter(`torch-${i}`, torch, NEVER));
    henchpeople.forEach((henchperson, i) =>
      this.townPlay.addCharacter(
        `henchperson-${i}`,
        henchperson,
        generateHenchpersonLoop$(henchperson),
        dialogueInteractionFn(t(`henchperson-${i}`, "hello"), overlay, `henchperson-${i}`)
      )
    );

    this.townPlay.addCharacter(
      "director-fran",
      director,
      NEVER,
      dialogueInteractionEventFn(
        ec,
        "king-president-arrives",
        t("director-fran", "they-are-coming"),
        overlay
      )
    );
    this.townPlay.addCharacter("scary-presto", scaryPresto, scaryLoop$);
    this.townPlay.addCharacter("henchpeople", null, henchpeopleLoop$);
    this.townPlay.addCharacter("royal-getems", null, getemsLoop$);
    this.townPlay.addCharacter("king-president", kingPresident, kingPresidentLoop$);
    this.townPlay.addCharacter("lord-egg", lordEgg, lordEggLoop$);
    this.townPlay.addCharacter("king-scary-convo", null, scaryKingConvoLoop$);
    this.townPlay.addCharacter("lucky-henchperson", null, luckyHenchpersonLoop$);
    this.townPlay.addCharacter("game-saver", null, gameSaverLoop$);
    this.townPlay.addCharacter("metaorb", metaorb, metaorbLoop$);

    getems.forEach((getem, i) => {
      this.townPlay.addCharacter(`getem-${i}`, getem, getEmLoops$[i]);
    });

    // +++ Add to sprite renderer

    this.spriteRenderer.add(director);
    henchpeople.forEach(henchperson => this.spriteRenderer.add(henchperson));
    torches.forEach(torch => this.spriteRenderer.add(torch));
    this.spriteRenderer.add(scaryPresto);
    this.spriteRenderer.add(kingPresident);
    this.spriteRenderer.add(lordEgg);
    this.spriteRenderer.add(metaorb);
    this.spriteRenderer.sort();
    getems.forEach(getem => this.spriteRenderer.add(getem));

    this.loaded = true;
  }

  update(time) {
    super.update(time);

    const diff = time - this.prevTorchTime;

    if (diff > torchSpeed) {
      this.torches.forEach((_, i) => (this.lightDropoffs[i] = randomNumberBetween(0.2, 0.8)));
      this.prevTorchTime = time;
    }

    updateRain(this);
  }

  resize() {
    super.resize();
    const dimensions = dimensionsMaintainingAspectWithMax(
      MAX_DIMENSION,
      state.aspect,
      window.innerWidth,
      window.innerHeight
    );
    const width = dimensions[0];
    const height = dimensions[1];
    resizeRain(this, width, height);
  }
}
